import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface OperationType {
  Id?: number;
  Name: string;
  Description?: string;
  Comments?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface OperationTypeListResponse {
  data: OperationType[];
  total: number;
}

export interface OperationTypeSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const operationTypeApi = {
  // CRUD Operations
  getAll: (params?: OperationTypeSearchParams): Promise<OperationTypeListResponse> =>
    api.get('/operationtype', params),

  getById: (id: number): Promise<OperationType> =>
    api.get(`/operationtype/${id}`),

  create: (operationType: Omit<OperationType, 'Id'>): Promise<OperationType> =>
    api.post('/operationtype', operationType),

  update: (id: number, operationType: Partial<OperationType>): Promise<OperationType> =>
    api.put(`/operationtype/${id}`, operationType),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/operationtype/${id}`),

  // Search and Filter
  search: (params: OperationTypeSearchParams): Promise<OperationTypeListResponse> =>
    api.post('/operationtype/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/operationtype/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/operationtype/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/operationtype/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/operationtype/statistics'),

  // Export
  exportToCSV: (params?: OperationTypeSearchParams): Promise<Blob> =>
    api.getBlob('/operationtype/export', { params }),

  // Validation
  validation: {
    validateOperationType: (operationType: Partial<OperationType>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!operationType.Name || operationType.Name.trim().length === 0) {
        errors.push('Operation Type Name is required');
      } else if (operationType.Name.trim().length < 2) {
        errors.push('Operation Type Name must be at least 2 characters long');
      } else if (operationType.Name.trim().length > 100) {
        errors.push('Operation Type Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (operationType.Description && operationType.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Comments validation (optional but if provided, validate length)
      if (operationType.Comments && operationType.Comments.trim().length > 1000) {
        errors.push('Comments must be less than 1000 characters');
      }

      // Active validation
      if (operationType.Active === undefined || operationType.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one Operation Type must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default operationTypeApi; 